<script setup lang="ts">
import { onMounted, watch, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import AdminLayout from '@/components/layout/AdminLayout.vue'
import PayoutStatusChip from '@/components/organisations/PayoutStatusChip.vue'
import { useOrganisations } from '@/composables/useOrganisations'
import { formatBytes } from '@/lib/storageFormat'
import type { AdminOrganisation } from '@/types/organisations'

const { t } = useI18n()
const router = useRouter()
const { items, meta, loading, error, search, page, fetchList } = useOrganisations()

// Debounce search
let searchDebounce: ReturnType<typeof setTimeout> | undefined

const searchInput = ref('')

watch(searchInput, (val) => {
  clearTimeout(searchDebounce)
  searchDebounce = setTimeout(() => {
    search.value = val
    page.value = 1
    void fetchList()
  }, 350)
})

// Over quota = counted usage exceeds the effective quota (a lowered quota); highlighted in red.
function isOverQuota(org: AdminOrganisation): boolean {
  return (org.storage_used_bytes ?? 0) > (org.storage_quota_bytes ?? Number.POSITIVE_INFINITY)
}

function goToDetail(id: number): void {
  void router.push({ name: 'admin.organisations.show', params: { id } })
}

function prevPage(): void {
  if (page.value > 1) {
    page.value--
    void fetchList()
  }
}

function nextPage(): void {
  if (meta.value && page.value < meta.value.last_page) {
    page.value++
    void fetchList()
  }
}

onMounted(fetchList)
</script>

<template>
  <AdminLayout>
    <div class="space-y-6">
      <div class="flex items-center justify-between">
        <h1 class="text-2xl font-bold text-white">{{ t('admin.organisations.title') }}</h1>
      </div>

      <!-- Search -->
      <div class="max-w-sm">
        <input
          v-model="searchInput"
          type="search"
          :placeholder="t('admin.organisations.search_placeholder')"
          class="w-full rounded-lg bg-slate-800 border border-slate-600 px-3 py-2 text-white text-sm placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-colors"
        />
      </div>

      <!-- Loading state -->
      <div v-if="loading" class="space-y-2">
        <div
          v-for="i in 5"
          :key="i"
          class="h-12 bg-slate-800 rounded-lg animate-pulse"
        />
      </div>

      <!-- Error state -->
      <div
        v-else-if="error"
        class="bg-red-500/10 border border-red-500/30 rounded-2xl p-6 flex items-center justify-between"
      >
        <p class="text-red-300 text-sm">{{ t('admin.common.error_loading') }}</p>
        <button
          type="button"
          class="text-sm text-red-300 hover:text-red-200 font-medium underline"
          @click="fetchList"
        >
          {{ t('admin.common.retry') }}
        </button>
      </div>

      <!-- Empty state -->
      <div
        v-else-if="items.length === 0"
        class="bg-slate-800 border border-slate-700 rounded-2xl p-12 text-center"
      >
        <p class="text-slate-400">{{ t('admin.organisations.no_results') }}</p>
      </div>

      <!-- Table -->
      <div v-else class="bg-slate-800 border border-slate-700 rounded-2xl overflow-hidden">
        <table class="w-full text-sm">
          <thead>
            <tr class="border-b border-slate-700">
              <th class="text-left px-4 py-3 text-slate-400 font-medium">{{ t('admin.organisations.name') }}</th>
              <th class="text-left px-4 py-3 text-slate-400 font-medium">{{ t('admin.organisations.city') }}</th>
              <th class="text-left px-4 py-3 text-slate-400 font-medium">{{ t('admin.organisations.events') }}</th>
              <th class="text-left px-4 py-3 text-slate-400 font-medium">{{ t('admin.organisations.members') }}</th>
              <th class="text-left px-4 py-3 text-slate-400 font-medium">{{ t('admin.organisations.payouts.column') }}</th>
              <th class="text-left px-4 py-3 text-slate-400 font-medium">{{ t('admin.organisations.storage_column') }}</th>
              <th class="text-left px-4 py-3 text-slate-400 font-medium">{{ t('admin.organisations.created_at') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="org in items"
              :key="org.id"
              class="border-b border-slate-700 last:border-0 hover:bg-slate-700/50 cursor-pointer transition-colors"
              @click="goToDetail(org.id)"
            >
              <td class="px-4 py-3 text-white font-medium">{{ org.name }}</td>
              <td class="px-4 py-3 text-slate-300">{{ org.city }}</td>
              <td class="px-4 py-3 text-slate-300">{{ org.events_count }}</td>
              <td class="px-4 py-3 text-slate-300">{{ org.users_count }}</td>
              <td class="px-4 py-3">
                <PayoutStatusChip :status="org.payout_account.status" />
              </td>
              <td
                class="px-4 py-3 text-xs whitespace-nowrap"
                :class="isOverQuota(org) ? 'text-red-400 font-medium' : 'text-slate-300'"
                data-testid="org-storage"
              >
                {{
                  org.storage_used_bytes !== undefined && org.storage_quota_bytes !== undefined
                    ? t('admin.organisations.storage_used_of_quota', {
                        used: formatBytes(org.storage_used_bytes),
                        quota: formatBytes(org.storage_quota_bytes),
                      })
                    : '-'
                }}
              </td>
              <td class="px-4 py-3 text-slate-400 text-xs">
                {{ new Date(org.created_at).toLocaleDateString() }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div v-if="meta && meta.last_page > 1" class="flex items-center justify-between text-sm text-slate-400">
        <span>{{ t('admin.common.page_of', { current: meta.current_page, total: meta.last_page }) }}</span>
        <div class="flex gap-2">
          <button
            type="button"
            :disabled="page <= 1"
            class="px-3 py-1.5 rounded-lg border border-slate-600 hover:border-slate-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            @click="prevPage"
          >
            {{ t('admin.common.previous') }}
          </button>
          <button
            type="button"
            :disabled="meta.current_page >= meta.last_page"
            class="px-3 py-1.5 rounded-lg border border-slate-600 hover:border-slate-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            @click="nextPage"
          >
            {{ t('admin.common.next') }}
          </button>
        </div>
      </div>
    </div>
  </AdminLayout>
</template>
